import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createPositionalQuestionIntoActivity,
  updatePositionalQuestionIntoActivity,
} from '@/data/services/question/positionalQuestionServices';
import { updatePositionalColumn } from '@/data/services/question/positionalColumnServices';
import Activity from '@/models/Activity';
import { DNDPositionalQuestion } from '@/models/Question';
import { QuestionResolutionError } from '@/errors/questionResolution';
import {
  adapterPositionalAnswers,
  adapterPositionalQuestions,
} from '@/utils/questions/adapterAnswersDTO';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import {
  sanitizePositionalAnswers,
  sanitizePositionalQuestions,
} from '@/utils/questions/sanitizeAnswers';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import CombinationForm, { CombinationFormFields } from './CombinationForm';

type CombinationFormServiceProps = {
  activity: Activity;
  question: DNDPositionalQuestion;
  isActive?: boolean;
};

export default function CombinationFormService({
  activity,
  question,
  isActive,
}: CombinationFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);
  const questionColumn = question.questionColumn;
  const answerColumn = question.answerColumn;

  const handleCreate = async (
    data: CombinationFormFields,
  ): Promise<DNDPositionalQuestion> => {
    const { maxGrade, ...rest } = data;
    const editableActivity = await ensureEditable();
    const createdActivity = await createPositionalQuestionIntoActivity(
      editableActivity.id,
      {
        type: question.type,
        identifier: question.identifier,
        order: question.order,
        title: rest.title,
        context: rest.context,
        feedbacks: sanitizeFeedbacks(rest.feedbacks),
      },
    );

    const [updatedQuestionColumn, updatedAnswerColumn] = await Promise.all([
      updatePositionalColumn(createdActivity.questionColumn.id, {
        items: sanitizePositionalQuestions(rest.questions),
      }),
      updatePositionalColumn(createdActivity.answerColumn.id, {
        items: sanitizePositionalAnswers(rest.answers),
      }),
    ]);

    const saved = {
      ...createdActivity,
      questionColumn: updatedQuestionColumn,
      answerColumn: updatedAnswerColumn,
    };
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: CombinationFormFields,
  ): Promise<DNDPositionalQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<CombinationFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      questions: adapterPositionalQuestions(questionColumn?.items ?? []),
      answers: adapterPositionalAnswers(answerColumn?.items ?? []),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const activityChanges: Partial<
      Pick<CombinationFormFields, 'title' | 'context' | 'feedbacks'>
    > = {};
    if (!isEqual(oldValues.title, rest.title)) {
      activityChanges.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      activityChanges.context = rest.context;
    }
    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      activityChanges.feedbacks = newSanitizedFeedbacks;
    }

    const oldSanitizedQuestions = sanitizePositionalQuestions(
      oldValues.questions,
    );
    const newSanitizedQuestions = sanitizePositionalQuestions(rest.questions);
    const questionsChanged = !isEqual(
      oldSanitizedQuestions,
      newSanitizedQuestions,
    );

    const oldSanitizedAnswers = sanitizePositionalAnswers(oldValues.answers);
    const newSanitizedAnswers = sanitizePositionalAnswers(rest.answers);
    const answersChanged = !isEqual(oldSanitizedAnswers, newSanitizedAnswers);

    const { target, toClone, updateTarget } = await resolveEditable(question);
    const targetQuestionColumn = target.questionColumn;
    const targetAnswerColumn = target.answerColumn;

    if (questionsChanged && questionColumn?.id && !targetQuestionColumn?.id)
      throw new QuestionResolutionError();
    if (answersChanged && answerColumn?.id && !targetAnswerColumn?.id)
      throw new QuestionResolutionError();

    const clonedQuestions =
      questionsChanged && targetQuestionColumn?.id
        ? toClone(newSanitizedQuestions, q => q.questionColumn?.items)
        : undefined;
    const clonedAnswers =
      answersChanged && targetAnswerColumn?.id
        ? toClone(newSanitizedAnswers, q => q.answerColumn?.items)
        : undefined;

    const updatedActivity = await updateTarget(
      activityChanges,
      updatePositionalQuestionIntoActivity,
    );

    const [updatedQuestionColumn, updatedAnswerColumn] = await Promise.all([
      clonedQuestions && targetQuestionColumn?.id
        ? updatePositionalColumn(targetQuestionColumn.id, {
            items: clonedQuestions,
          })
        : Promise.resolve(targetQuestionColumn),
      clonedAnswers && targetAnswerColumn?.id
        ? updatePositionalColumn(targetAnswerColumn.id, {
            items: clonedAnswers,
          })
        : Promise.resolve(targetAnswerColumn),
    ]);

    const saved = {
      ...updatedActivity,
      questionColumn: updatedQuestionColumn,
      answerColumn: updatedAnswerColumn,
    };

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: CombinationFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <CombinationForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
